/**
 * Socket.io temps réel pour /b/[slug] :
 *  - écoute des ventes de billets en live (toast + soldCountOverrides)
 *  - détection de conflit si l'acheteur a déjà cliqué Continuer
 */
import { quantiteEnDirect, useAnnonceDerniereActivite, useToastEnDirect } from "~/composables/useToastEnDirect";
import { io, type Socket } from "socket.io-client";
import type { Event, TicketType } from "~/types/event";

export function useEventTicketSocket(opts: {
  event: Ref<Event | null>;
  ticketTypes: Ref<TicketType[]>;
  selections: Ref<Record<string, number>>;
  totalSelected: Ref<number>;
  checkoutStep: Ref<number>;
  soldCountOverrides: Ref<Record<string, number>>;
}) {
  const { t } = useI18n();
  const config = useRuntimeConfig();
  const toast = useToast();
  const { annoncerEnDirect } = useToastEnDirect();
  // Dernière réservation (moins de 7 jours) annoncée à l'arrivée.
  useAnnonceDerniereActivite(
    () => opts.event.value?.derniere_activite,
    (_a, recente) => t(recente ? "commun.enDirect.vientDeReserver" : "commun.enDirect.aReserve"),
    "i-lucide-ticket",
  );
  let eventSocket: Socket | null = null;
  // Billets vendus par tarif, au dernier état connu. Par TARIF et non en
  // total : la page ne reçoit que les tarifs encore en vente, le socket les
  // pousse tous — un total mélangé annoncerait des ventes fantômes. Un tarif
  // vu pour la première fois sert de point de départ, sans annonce.
  const venduParTarif = new Map<string, number>();

  onMounted(() => {
    // Connexion dès que l'événement est connu — y compris s'il arrive APRÈS le
    // mount (retry client suite à une erreur SSR transitoire). Avant, un mount
    // sans event = jamais de socket → compteurs figés. Init unique via flag
    // (PAS stop() dans le callback : avec `immediate` il s'exécute avant que
    // watch() retourne). Karim 2026-08-04.
    let initialized = false;
    watch(
      () => opts.event.value?.id,
      (eventId) => {
        if (!eventId || initialized) return;
        initialized = true;
        initSocket(eventId);
      },
      { immediate: true },
    );
  });

  function initSocket(eventId: string) {
    for (const tt of opts.ticketTypes.value) venduParTarif.set(tt.id, tt.sold_count || 0);

    const socketUrl = config.public.apiBaseUrl || "http://localhost:4000";
    eventSocket = io(socketUrl, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
    });

    eventSocket.on("connect", () => {
      eventSocket!.emit("join:event", eventId);
    });

    eventSocket.on("event:ticket_update", (data: { eventId: string; ticket_types: Array<{ id: string; sold_count: number }> }) => {
      if (data.eventId !== opts.event.value?.id) return;

      let soldDiff = 0;
      for (const tt of data.ticket_types) {
        const avant = venduParTarif.get(tt.id);
        if (avant !== undefined && tt.sold_count > avant) soldDiff += tt.sold_count - avant;
        venduParTarif.set(tt.id, tt.sold_count);
      }

      if (soldDiff > 0) {
        annoncerEnDirect(
          t("commun.enDirect.vientDeReserverQuantite", {
            quantite: quantiteEnDirect(soldDiff, t("billetterie.partage.billetSingulier"), t("billetterie.partage.billetPluriel")),
          }),
          "i-lucide-ticket",
        );
      }

      // Snapshot des quantités sélectionnées AVANT que les overrides ne
      // déclenchent le clamp réactif (useEventTicketSelection), pour détecter
      // qu'un tarif sélectionné vient de devenir complet/insuffisant.
      const before: Record<string, number> = { ...opts.selections.value };

      const overrides: Record<string, number> = {};
      for (const tt of data.ticket_types) overrides[tt.id] = tt.sold_count;
      opts.soldCountOverrides.value = overrides;

      // Le clamp réactif réduit déjà les quantités à la nouvelle dispo (toutes
      // étapes, capés ou non). Ici on ne fait QUE le feedback : détecter si une
      // ligne sélectionnée a dû être réduite/zéroée, prévenir l'acheteur, et le
      // ramener à l'étape 1 s'il était plus loin. On laisse le clamp être la
      // seule autorité de mutation (pas de double-clamp). Karim 2026-06-23.
      let reduced = false;
      for (const [typeId, qty] of Object.entries(before)) {
        if (qty <= 0) continue;
        const ttData = data.ticket_types.find(t => t.id === typeId);
        const ttInfo = opts.ticketTypes.value.find(t => t.id === typeId);
        if (!ttData || !ttInfo) continue;
        if (ttInfo.max_quantity && ttData.sold_count + qty > ttInfo.max_quantity) {
          reduced = true;
          break;
        }
      }
      if (!reduced && opts.event.value?.max_capacity) {
        const newTotal = data.ticket_types.reduce((s, t) => s + t.sold_count, 0);
        if (newTotal + opts.totalSelected.value > opts.event.value.max_capacity) {
          reduced = true;
        }
      }
      if (reduced) {
        toast.add({
          title: t("billetterie.useEventTicketSocket.placesVenduesTitre"),
          description: t("billetterie.partage.selectionAjustee"),
          color: "warning",
          duration: 6000,
        });
        if (opts.checkoutStep.value > 1) opts.checkoutStep.value = 1;
      }
    });
  }

  onUnmounted(() => {
    if (eventSocket) {
      if (opts.event.value?.id) eventSocket.emit("leave:event", opts.event.value.id);
      eventSocket.disconnect();
      eventSocket = null;
    }
  });
}
